import { Button, Card, Form, Input, message, Space, Switch, Typography } from "antd";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import CustomButton from "../common/CustomButton";
import { useAuth } from "../config/AuthProvider";
import { useGoBack } from "../customHooks/useGoBack";
import { CountryCodeSelect, PhoneCodeSelect } from "../helpers/common";
import OrgService from "../services/Org/OrgService";
import AppButton from "../common/AppButton";
import ConfirmDeleteButton from "../common/actions/ConfirmDeleteButton";

const { TextArea } = Input;
const { Title } = Typography;

const NewOrg = () => {
  const { user } = useAuth();
  const location = useLocation();
  const mode = location?.state?.mode || "add";
  const org = location?.state?.org || {};
  const isOrgAdmin = user?.role === "OrgAdmin";
  const [form] = Form.useForm();
  const goBack = useGoBack();

  useEffect(() => {
    const initializeData = () => {
      resetForm();
      if (isOrgAdmin) {
        fetchClientOrgDetails();
      } else if (mode === "edit" && org) {
        setFormData(org);
      }
    };
    initializeData();
  }, [isOrgAdmin, mode, org]);

  const fetchClientOrgDetails = () => {
    OrgService.getClientOrgDetails()
      .then((response) => {
        if (response.status === 200) {
          setFormData(response.data);
        }
      })
      .catch((error) => {
        message.error(error?.response?.data?.message || "Something went wrong");
        console.error("Error Fetching Org Details:", error);
      });
  };

  const setFormData = (org) => {
    form.setFieldsValue({
      id: org.id,
      name: org.name,
      supplierShortCode: org.supplierShortCode,
      email: org.email,
      phoneCode: org.phoneCode,
      phoneNumber: org.phoneNumber,
      currencyCd: org.currencyCd,
      addressLine1: org.address.addressLine1,
      addressLine2: org.address.addressLine2,
      zipCode: org.address.zipCode,
      city: org.address.city,
      state: org.address.state,
      countryCd: org.address.countryCd,
      defaultPOL: org.defaultPOL,
      paymentMethodType: org.paymentMethodType,
      subscriptionType: org.subscriptionType,
      isActive: org.isActive,
    });
  }

  const resetForm = () => {
    form.resetFields();
  };

  const handleClose = () => {
    goBack();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const request = {
        ...values,
        orgType: "Client",
      };
      var response;
      if (mode === "edit" || isOrgAdmin) {
        response = await OrgService.updateOrgSupplier(request);
      } else {
        response = await OrgService.createOrgSupplier(request);
      }
      if (response?.status === 200) {
        message.success(`Org ${(mode === "edit" || isOrgAdmin) ? "Updated" : "Added"} Successfully`);
        handleClose();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  return (
    <Form form={form} layout="vertical" className="px-8 mb-4">
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <div className="flex justify-start items-center w-full my-4 mx-2 font-inter">
        <Title
          level={2}
          className="!font-medium !text-[#015373] !m-0 pl-4 mt-10"
        >
          {isOrgAdmin ? "Org Profile" : (mode === "edit" ? "Org Details " : "New Org")}
        </Title>
        <CustomButton
          type="link"
          onClick={goBack}
          className="text-md font-medium !text-[#01A4A6] pr-4 pt-2"
        >
          / Org
        </CustomButton>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-start">
        <div className="col-span-2 flex flex-col gap-4">
          <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80">
            <div className="mt-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="name"
                label="Org Name"
                rules={[
                  { required: true, message: "Please Enter Org Name" },
                ]}
              >
                <Input size="large" />
              </Form.Item>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="supplierShortCode"
                label="Short Code"
                rules={[
                  { required: true, message: "Please Enter Short Code" },
                  { max: 10, message: "Short Code must be at most 10 characters" }
                ]}
              >
                <Input size="large" maxLength={10} disabled={isOrgAdmin} />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please Enter Email" },
                  { type: "email", message: "Invalid Email" },
                ]}
              >
                <Input size="large" className="!h-[34px]" />
              </Form.Item>
              <Form.Item label="Phone Number" required>
                <Space.Compact className="w-full">
                  <Form.Item
                    name="phoneCode"
                    noStyle
                    rules={[{ required: true, message: "Select Phone Code" }]}
                    initialValue="61"
                  >
                    <PhoneCodeSelect selectClassNames="!w-32 !h-[34px] text-center" />
                  </Form.Item>
                  <Form.Item
                    name="phoneNumber"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "Enter Phone Number",
                        min: 1,
                        max: 15,
                      },
                    ]}
                  >
                    <Input size="large" className="!h-[34px]" />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </div>
          </Card>
          <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80">
            <div className="mt-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="addressLine1"
                label="Address Line 1"
                rules={[
                  {
                    required: true,
                    message: "Please Enter Suite / Floor / Building etc.",
                  },
                ]}
              >
                <TextArea size="large" rows={1} autoSize />
              </Form.Item>
              <Form.Item name="addressLine2" label="Address Line 2">
                <TextArea rows={1} autoSize size="large" />
              </Form.Item>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="zipCode"
                label="ZIP Code / Postcode"
                rules={[
                  {
                    required: true,
                    message: "Please Enter ZIP Code / Postcode",
                  },
                ]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: "Please Enter City" }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name="state"
                label="State / District"
                rules={[
                  { required: true, message: "Please Enter State / District" },
                ]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name="countryCd"
                label="Country"
                rules={[{ required: true, message: "Please Select Country" }]}
              >
                <CountryCodeSelect />
              </Form.Item>
            </div>
          </Card>
        </div>
        <Card className="pb-1 rounded-3xl shadow-xl col-span-2 bg-white bg-opacity-80">
          <div className="mt-2 grid grid-cols-2 gap-x-16 ml-10 mr-10">
            {(mode === "edit" || isOrgAdmin) && (
              <Form.Item
                name="isActive"
                label="Active Status"
                valuePropName="checked"
                className="col-span-2 !w-1/2 pr-8"
                layout="horizontal"
              >
                <Switch className="flex justify-self-end" disabled={isOrgAdmin} />
              </Form.Item>
            )}
          </div>
          <div className="gap-x-16 ml-10 mr-10">
            <Form.Item name="paymentMethodType" label="Payment Method Type">
              <Input size="large" />
            </Form.Item>
            <Form.Item name="subscriptionType" label="Subscription Type">
              <Input size="large" />
            </Form.Item>
          </div>
        </Card>
      </div>
      <div className="flex justify-end mr-8">
        <AppButton variant="save" onClick={handleSubmit}>Save</AppButton>
        {mode === "edit" ? (
            <ConfirmDeleteButton
                title={
                  <>
                    {`You are about to delete ${""} Org.`}
                    <br />
                    Please confirm you wish to proceed.
                  </>
                }
                onConfirm={() => {}}
                okText="Yes"
                cancelText="No"
              />
        ) : null}
        <AppButton variant="close" onClick={handleClose}>Cancel</AppButton>
      </div>
    </Form>
  );
};

export default NewOrg;